import html2canvas from 'html2canvas';
import { wsService } from './WebSocketService';
import { logger } from './Logger';

const log = logger.child('Recording');

// Configuration
export interface RecordingConfig {
  enabled: boolean;
  frameRate: number;        // Lower for full-page capture (10 FPS recommended)
  videoBitrate: number;
  bufferDuration: number;   // seconds
  chunkInterval: number;    // ms
  postBuffer: number;       // seconds after trade close
  captureMode: 'canvas' | 'fullpage';  // canvas = chart only, fullpage = entire container
}

const DEFAULT_CONFIG: RecordingConfig = {
  enabled: true,
  frameRate: 10,            // 10 FPS for full-page to reduce CPU
  videoBitrate: 2_500_000,  // 2.5 Mbps
  bufferDuration: 60,       // 60 seconds
  chunkInterval: 1000,      // 1 second chunks
  postBuffer: 10,           // 10 seconds after close
  captureMode: 'fullpage',  // Default to full page capture
};

interface ChunkData {
  timestamp: number;
  blob: Blob;
}

interface ActiveRecording {
  sessionId: string;
  symbol: string;
  openTime: number;
  chunkIndex: number;
  postBufferTimeout?: ReturnType<typeof setTimeout>;
}

/**
 * RecordingService - Captures trading view for trade recordings
 *
 * Supports two modes:
 * - 'canvas': Captures chart canvas only (low CPU, 30 FPS)
 * - 'fullpage': Captures entire container using html2canvas (higher CPU, 10 FPS)
 *
 * Uses browser MediaRecorder API to maintain a rolling buffer of the last 60 seconds.
 * When a trade opens, streams chunks to the backend.
 * When a trade closes, waits for post-buffer then signals backend to save.
 */
class RecordingService {
  private config: RecordingConfig = DEFAULT_CONFIG;
  private targetElement: HTMLElement | null = null;
  private recordingCanvas: HTMLCanvasElement | null = null;
  private stream: MediaStream | null = null;
  private mediaRecorder: MediaRecorder | null = null;
  private rollingBuffer: ChunkData[] = [];
  private activeRecording: ActiveRecording | null = null;
  private isCapturing = false;
  private pruneInterval: ReturnType<typeof setInterval> | null = null;
  private captureInterval: ReturnType<typeof setInterval> | null = null;
  private captureInProgress = false;

  /**
   * Initialize the recording service with target element
   * @param element - For 'canvas' mode: the canvas element. For 'fullpage' mode: the container element
   */
  initialize(element: HTMLElement, config?: Partial<RecordingConfig>): boolean {
    if (!element) {
      log.warn('No element provided for recording');
      return false;
    }

    if (!this.isMediaRecorderSupported()) {
      log.warn('MediaRecorder API not supported in this browser');
      return false;
    }

    this.config = { ...DEFAULT_CONFIG, ...config };
    this.targetElement = element;

    // For fullpage mode, create an offscreen canvas
    if (this.config.captureMode === 'fullpage') {
      this.recordingCanvas = document.createElement('canvas');
      // Will be resized on first capture
    } else {
      // For canvas mode, use the element directly
      this.recordingCanvas = element as HTMLCanvasElement;
    }

    log.info('RecordingService initialized', {
      mode: this.config.captureMode,
      frameRate: this.config.frameRate,
      elementSize: `${element.clientWidth}x${element.clientHeight}`,
    });

    return true;
  }

  /**
   * Start continuous capture (rolling buffer)
   */
  startCapture(): boolean {
    if (!this.targetElement || !this.recordingCanvas) {
      log.warn('Cannot start capture: no element initialized');
      return false;
    }

    if (this.isCapturing) {
      log.debug('Already capturing');
      return true;
    }

    try {
      if (this.config.captureMode === 'fullpage') {
        return this.startFullPageCapture();
      } else {
        return this.startCanvasCapture();
      }
    } catch (error) {
      log.error('Failed to start capture', error instanceof Error ? error : undefined);
      return false;
    }
  }

  /**
   * Start canvas-only capture (original approach)
   */
  private startCanvasCapture(): boolean {
    const canvas = this.targetElement as HTMLCanvasElement;

    // Capture stream from canvas
    this.stream = canvas.captureStream(this.config.frameRate);

    // Find supported mime type
    const mimeType = this.getSupportedMimeType();
    if (!mimeType) {
      log.error('No supported video mime type found');
      return false;
    }

    // Create MediaRecorder
    this.mediaRecorder = new MediaRecorder(this.stream, {
      mimeType,
      videoBitsPerSecond: this.config.videoBitrate,
    });

    this.setupRecorderHandlers();
    this.mediaRecorder.start(this.config.chunkInterval);
    this.isCapturing = true;

    // Start buffer pruning
    this.pruneInterval = setInterval(() => this.pruneBuffer(), 5000);

    log.info('Started canvas capture', { mimeType, frameRate: this.config.frameRate });
    return true;
  }

  /**
   * Start full-page capture using html2canvas
   */
  private startFullPageCapture(): boolean {
    if (!this.recordingCanvas) return false;

    // Initial canvas size
    this.recordingCanvas.width = this.targetElement!.clientWidth;
    this.recordingCanvas.height = this.targetElement!.clientHeight;

    // Capture stream from our recording canvas
    this.stream = this.recordingCanvas.captureStream(this.config.frameRate);

    // Find supported mime type
    const mimeType = this.getSupportedMimeType();
    if (!mimeType) {
      log.error('No supported video mime type found');
      return false;
    }

    // Create MediaRecorder
    this.mediaRecorder = new MediaRecorder(this.stream, {
      mimeType,
      videoBitsPerSecond: this.config.videoBitrate,
    });

    this.setupRecorderHandlers();
    this.mediaRecorder.start(this.config.chunkInterval);
    this.isCapturing = true;

    // Start periodic DOM capture
    const captureIntervalMs = 1000 / this.config.frameRate;
    this.captureInterval = setInterval(() => this.captureFrame(), captureIntervalMs);

    // Start buffer pruning
    this.pruneInterval = setInterval(() => this.pruneBuffer(), 5000);

    log.info('Started full-page capture', {
      mimeType,
      frameRate: this.config.frameRate,
      captureInterval: `${captureIntervalMs}ms`,
    });
    return true;
  }

  /**
   * Capture a single frame using html2canvas
   */
  private async captureFrame(): Promise<void> {
    if (!this.targetElement || !this.recordingCanvas || this.captureInProgress) {
      return;
    }

    this.captureInProgress = true;

    try {
      // Render DOM to canvas
      const capturedCanvas = await html2canvas(this.targetElement, {
        canvas: this.recordingCanvas,
        backgroundColor: null,
        scale: 1,
        logging: false,
        useCORS: true,
        allowTaint: true,
        // Performance optimizations
        imageTimeout: 0,
        removeContainer: true,
      });

      // Ensure dimensions match
      if (capturedCanvas !== this.recordingCanvas) {
        const ctx = this.recordingCanvas.getContext('2d');
        if (ctx) {
          this.recordingCanvas.width = capturedCanvas.width;
          this.recordingCanvas.height = capturedCanvas.height;
          ctx.drawImage(capturedCanvas, 0, 0);
        }
      }
    } catch (error) {
      // Silently fail on capture errors to not spam logs
    } finally {
      this.captureInProgress = false;
    }
  }

  /**
   * Setup MediaRecorder event handlers
   */
  private setupRecorderHandlers(): void {
    if (!this.mediaRecorder) return;

    this.mediaRecorder.ondataavailable = (event) => {
      if (event.data.size > 0) {
        this.handleChunk(event.data);
      }
    };

    this.mediaRecorder.onerror = (event) => {
      log.error('MediaRecorder error', event.error instanceof Error ? event.error : undefined);
    };
  }

  /**
   * Stop capture
   */
  stopCapture(): void {
    if (this.captureInterval) {
      clearInterval(this.captureInterval);
      this.captureInterval = null;
    }

    if (this.mediaRecorder && this.mediaRecorder.state !== 'inactive') {
      this.mediaRecorder.stop();
    }

    if (this.stream) {
      this.stream.getTracks().forEach(track => track.stop());
    }

    if (this.pruneInterval) {
      clearInterval(this.pruneInterval);
      this.pruneInterval = null;
    }

    this.mediaRecorder = null;
    this.stream = null;
    this.isCapturing = false;
    this.rollingBuffer = [];

    log.info('Stopped capture');
  }

  /**
   * Handle trade open event - start streaming to backend
   */
  onTradeOpen(symbol: string, timestamp: number): void {
    if (!this.config.enabled || !this.isCapturing) {
      log.debug('Recording disabled or not capturing, skipping trade open');
      return;
    }

    if (this.activeRecording) {
      log.warn('Trade already being recorded, ignoring new open', {
        existing: this.activeRecording.symbol,
        new: symbol,
      });
      return;
    }

    const sessionId = `${symbol}-${timestamp}-${Math.random().toString(36).substring(7)}`;

    this.activeRecording = {
      sessionId,
      symbol,
      openTime: timestamp,
      chunkIndex: 0,
    };

    log.info('Trade opened, starting recording', { sessionId, symbol });

    // Signal backend
    wsService.emit('recording:start', {
      sessionId,
      symbol,
      timestamp,
    });

    // Flush buffer (pre-trade context)
    this.flushBufferToBackend();
  }

  /**
   * Handle trade close event - schedule save after post-buffer
   */
  onTradeClose(symbol: string, timestamp: number): void {
    if (!this.activeRecording) {
      log.debug('No active recording for trade close');
      return;
    }

    if (this.activeRecording.symbol !== symbol) {
      log.warn('Trade close symbol mismatch', {
        active: this.activeRecording.symbol,
        closing: symbol,
      });
      return;
    }

    log.info('Trade closed, waiting for post-buffer', {
      sessionId: this.activeRecording.sessionId,
      postBuffer: this.config.postBuffer,
    });

    // Signal backend that trade is closed
    wsService.emit('recording:stop', {
      sessionId: this.activeRecording.sessionId,
      timestamp,
    });

    // Wait for post-buffer then signal save
    this.activeRecording.postBufferTimeout = setTimeout(() => {
      this.saveRecording();
    }, this.config.postBuffer * 1000);
  }

  /**
   * Get recording status
   */
  getStatus(): {
    isCapturing: boolean;
    isRecording: boolean;
    bufferDuration: number;
    bufferSize: number;
    activeSymbol: string | null;
    captureMode: string;
  } {
    const bufferDuration = this.rollingBuffer.length > 0
      ? (Date.now() - this.rollingBuffer[0].timestamp) / 1000
      : 0;

    const bufferSize = this.rollingBuffer.reduce((acc, chunk) => acc + chunk.blob.size, 0);

    return {
      isCapturing: this.isCapturing,
      isRecording: this.activeRecording !== null,
      bufferDuration,
      bufferSize,
      activeSymbol: this.activeRecording?.symbol ?? null,
      captureMode: this.config.captureMode,
    };
  }

  // --- Private methods ---

  private handleChunk(blob: Blob): void {
    const chunkData: ChunkData = {
      timestamp: Date.now(),
      blob,
    };

    // Add to rolling buffer
    this.rollingBuffer.push(chunkData);

    // If actively recording a trade, stream to backend
    if (this.activeRecording) {
      this.sendChunkToBackend(chunkData);
    }
  }

  private pruneBuffer(): void {
    const cutoff = Date.now() - (this.config.bufferDuration * 1000);
    const before = this.rollingBuffer.length;
    this.rollingBuffer = this.rollingBuffer.filter(chunk => chunk.timestamp > cutoff);

    if (before !== this.rollingBuffer.length) {
      log.debug('Pruned buffer', {
        removed: before - this.rollingBuffer.length,
        remaining: this.rollingBuffer.length,
      });
    }
  }

  private flushBufferToBackend(): void {
    if (!this.activeRecording) return;

    log.info('Flushing buffer to backend', {
      chunks: this.rollingBuffer.length,
      sessionId: this.activeRecording.sessionId,
    });

    for (const chunk of this.rollingBuffer) {
      this.sendChunkToBackend(chunk);
    }
  }

  private sendChunkToBackend(chunk: ChunkData): void {
    if (!this.activeRecording) return;

    const reader = new FileReader();
    reader.onload = () => {
      if (!this.activeRecording) return;

      const base64 = reader.result as string;
      // Remove data URL prefix (e.g., "data:video/webm;base64,")
      const data = base64.split(',')[1];

      wsService.emit('recording:chunk', {
        sessionId: this.activeRecording.sessionId,
        chunkIndex: this.activeRecording.chunkIndex++,
        timestamp: chunk.timestamp,
        data,
      });
    };

    reader.readAsDataURL(chunk.blob);
  }

  private saveRecording(): void {
    if (!this.activeRecording) return;

    log.info('Saving recording', { sessionId: this.activeRecording.sessionId });

    wsService.emit('recording:save', {
      sessionId: this.activeRecording.sessionId,
    });

    // Clear active recording
    if (this.activeRecording.postBufferTimeout) {
      clearTimeout(this.activeRecording.postBufferTimeout);
    }
    this.activeRecording = null;
  }

  private isMediaRecorderSupported(): boolean {
    return typeof MediaRecorder !== 'undefined';
  }

  private getSupportedMimeType(): string | null {
    const types = [
      'video/webm;codecs=vp9',
      'video/webm;codecs=vp8',
      'video/webm',
      'video/mp4',
    ];

    for (const type of types) {
      if (MediaRecorder.isTypeSupported(type)) {
        return type;
      }
    }

    return null;
  }

  /**
   * Test capture - for debugging
   * Returns a blob URL of a short recording
   */
  async testCapture(durationMs: number = 5000): Promise<string | null> {
    if (!this.recordingCanvas && !this.targetElement) {
      log.error('No element for test capture');
      return null;
    }

    return new Promise((resolve) => {
      let testCanvas: HTMLCanvasElement;
      let captureInterval: ReturnType<typeof setInterval> | null = null;

      if (this.config.captureMode === 'fullpage' && this.targetElement) {
        // For fullpage, create temp canvas and capture periodically
        testCanvas = document.createElement('canvas');
        testCanvas.width = this.targetElement.clientWidth;
        testCanvas.height = this.targetElement.clientHeight;
      } else {
        testCanvas = (this.targetElement || this.recordingCanvas) as HTMLCanvasElement;
      }

      // Use captureStream(0) for manual frame capture - required for html2canvas
      const stream = testCanvas.captureStream(0);
      const videoTrack = stream.getVideoTracks()[0] as any;

      if (this.config.captureMode === 'fullpage' && this.targetElement) {
        // Start capturing frames sequentially (html2canvas is slow, ~200-300ms per frame)
        let capturing = true;
        const captureLoop = async () => {
          while (capturing) {
            try {
              await html2canvas(this.targetElement!, {
                canvas: testCanvas,
                backgroundColor: null,
                scale: 1,
                logging: false,
                useCORS: true,
                allowTaint: true,
              });
              // Manually request frame after html2canvas renders
              if (videoTrack && videoTrack.requestFrame) {
                videoTrack.requestFrame();
              }
            } catch (e) {
              // Ignore errors
            }
            // Small delay between frames (html2canvas needs ~200-300ms anyway)
            await new Promise(r => setTimeout(r, 50));
          }
        };

        captureLoop();
        // Store cleanup function
        (captureInterval as any) = { stop: () => { capturing = false; } };
      }
      const mimeType = this.getSupportedMimeType();

      if (!mimeType) {
        log.error('No supported mime type for test');
        if (captureInterval && (captureInterval as any).stop) {
          (captureInterval as any).stop();
        }
        resolve(null);
        return;
      }

      const chunks: Blob[] = [];
      const recorder = new MediaRecorder(stream, { mimeType });

      recorder.ondataavailable = (e) => {
        if (e.data.size > 0) chunks.push(e.data);
      };

      recorder.onstop = () => {
        if (captureInterval && (captureInterval as any).stop) {
          (captureInterval as any).stop();
        }
        stream.getTracks().forEach(t => t.stop());
        const blob = new Blob(chunks, { type: mimeType });
        const url = URL.createObjectURL(blob);
        log.info('Test capture complete', { size: blob.size, url });
        resolve(url);
      };

      recorder.start(1000);
      setTimeout(() => recorder.stop(), durationMs);
    });
  }
}

// Singleton export
export const recordingService = new RecordingService();

// Expose to window for debugging
if (typeof window !== 'undefined') {
  (window as any).recordingService = recordingService;
}
